import { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Tabs, useBreakpointValue, useDisclosure, useToast } from "@chakra-ui/react";
import { TabStepsList, TabStepsPanels } from "./components/Steps";
import { t } from "i18next";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import * as React from "react";
import {
  organizationQuestionnaireKeys,
  useDefaultOrganizationQuestionnaireConfig,
  useFinalizeQuestionnaire
} from "../../../../hooks/query";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { useDispatch } from "react-redux";
import { setAction } from "../../../../context/Store/slices/questionnaireSlice";
import { CHANGE_ACTIONS } from "./components/Steps/TabStepsPanels";
import { useQueryClient } from "react-query";

function CategoryQuestionnaire({
                                 defaultValues,
                                 handleTabChange,
                                 hasPrevScope,
                                 returnToListRequested,
                                 returnToList,
                                 declineReturnToListRequest,
                                 allowedToSubmit,
                                 isPreview
                               }) {
  const [categoryTabIndex, setCategoryTabIndex] = useState(0);
  const [finalize, setFinalize] = useState(false);
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const saveBtnRef = useRef();

  const categoriesTabsState = useBreakpointValue({ base: true, xl: false });
  const [categoryTabsCollapsed, setCategoryTabsCollapsed] = useState(false);

  useEffect(() => {
    setCategoryTabsCollapsed(categoriesTabsState);
  }, [categoriesTabsState]);

  useEffect(() => {
    if (allowedToSubmit === true && returnToListRequested === true) {
      saveBtnRef.current.click();
    }
  }, [allowedToSubmit, returnToListRequested]);

  const getActiveCategories = () => {
    const categories = defaultValues.configurations;
    const activeCategories = [];
    Object.keys(categories)
      .filter(q => categories[q] && q !== "directEmissionsShouldBeFilled" && q !== "indirectEmissionsShouldBeFilled")
      .map(cat => {
        const _q = cat.substring(0, cat.indexOf("ShouldBe"));
        return activeCategories.push(_q);
      });
    return activeCategories;
  };

  const handleTabsCollapse = (areCollapsed) => {
    setCategoryTabsCollapsed(areCollapsed);
  };

  const handleFinalizeConfirmation = () => {
    const currentForm = document.getElementById(`formStep${getActiveCategories()[categoryTabIndex]}`);
    if (currentForm) {
      currentForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      handleDialogClose(true);
    }
  };

  const handleDialogClose = (confirmed) => {
    if (confirmed !== true) {
      setFinalize(false);
    }
    onConfirmDialogClose();
  };

  const { mutate: finalizeQuestionnaire } = useFinalizeQuestionnaire({
    ...useDefaultOrganizationQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, t("organizationQuestionnaire.page.finalized"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
      returnToList();
    },
    onError: err => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      setFinalize(false);
    }
  });

  return (
    <Box>
      <Tabs
        variant="unstyled"
        orientation="vertical"
        overflow="hidden"
        position="relative"
        isLazy
        isManual
        index={categoryTabIndex}
        maxBlockSize={{ base: "none", lg: "lg" }}
        marginInlineEnd="-4"
        minHeight={"600px"}
      >
        <TabStepsList
          currentIndex={categoryTabIndex}
          categories={getActiveCategories()}
          tabsCollapsed={categoryTabsCollapsed}
          setTabsCollapsed={handleTabsCollapse}
        />
        <TabStepsPanels
          defaultValues={defaultValues}
          onTabChange={setCategoryTabIndex}
          categories={getActiveCategories()}
          finalize={finalize}
          tabsCollapsed={categoryTabsCollapsed}
          finalizeQuestionnaire={finalizeQuestionnaire}
          onSectionChange={() => handleTabChange(prev => prev - 1)}
          setFinalize={setFinalize}
          returnToListRequested={returnToListRequested}
          returnToList={returnToList}
          declineReturnToListRequest={declineReturnToListRequest}
          isPreview={isPreview}
        />
      </Tabs>
      {!isPreview &&
        <Flex
          marginBlockStart="10"
          justifyContent={"space-between"}
          position="sticky"
          bottom="0"
          backgroundColor="#fff"
          borderColor="esg_gray.100"
          bordersyle="solid"
          borderTopWidth="1px"
          zIndex="10"
          paddingBlock="4"
        >
          <Box>
            {hasPrevScope && (
              <Button
                form={`formStep${getActiveCategories()[categoryTabIndex]}`}
                colorScheme="white"
                color="esg_lightgreen"
                border={"1px solid"}
                borderColor={"esg_lightgreen"}
                type="submit"
                onClick={() => {
                  hasPrevScope && dispatch(setAction(CHANGE_ACTIONS.PREV_SECTION));
                }}
                width="32"
              >
                {t("buttons.previous")}
              </Button>
            )}
          </Box>
          <Flex gap="2">
            <Button
              form={`formStep${getActiveCategories()[categoryTabIndex]}`}
              colorScheme="white"
              color="esg_lightgreen"
              border={"1px solid"}
              borderColor={"esg_lightgreen"}
              type="submit"
              width="32"
              ref={saveBtnRef}
            >
              {t("buttons.save")}
            </Button>
            <Button
              form={`formStep${getActiveCategories()[categoryTabIndex]}`}
              colorScheme="customScheme"
              width="32"
              onClick={() => {
                setFinalize(true);
                onConfirmDialogOpen();
              }}
            >
              {t("buttons.finalize")}
            </Button>
          </Flex>
        </Flex>
      }
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
    </Box>
  );
}

export default CategoryQuestionnaire;
